import React, { useCallback, useEffect, useRef, useState } from "react";
import {
    Box,
    Card,
    CardContent,
    CardHeader,
    Checkbox,
    Collapse,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TableSortLabel,
    Typography
} from "@mui/material";
import InputBase from '@mui/material/InputBase';
import SearchIcon from '@mui/icons-material/Search';
import CircularProgress from '@mui/material/CircularProgress';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import { makeStyles, withStyles } from '@mui/styles';
import { useTranslation } from "react-i18next";
import NativeSelect from '@mui/material/NativeSelect';
import DataTableManageButton from "./DataTableManageButton";
import TableRowSelection from "./board/TableRowSelection";
import { KeyboardArrowDown, KeyboardArrowRight, KeyboardArrowUp } from "@mui/icons-material";
import { de } from "date-fns/locale";

const StyledTableCell = withStyles((theme) => ({
    head: {
        backgroundColor: '#EFEBFF',
        color: '#26415c',
        fontWeight: 'bold'
    }
}))(TableCell);

const SearchInput = withStyles((theme) => ({
    root: {
        width: '100%',
    },
    input: {
        width: '100%',
    }
}))(InputBase);

const NoPaddingTableCell = withStyles({
    root: {
        padding: '0 !important',
    },
})(TableCell);

const NoPaddingTableRow = withStyles({
    root: {
        padding: 0,
        margin: 0,
    },
})(TableRow);

const SearchSelect = withStyles((theme) => ({
    root: {
        width: '100%',
        border: "none"
    }
}))(NativeSelect);

const useStyles = makeStyles((theme) => ({
    card: {
        height: '100%',
    },
    title: {
        fontSize: '1.2rem'
    },
    root: {
        paddingBottom: '0px',
    },
    contentRoot: {
        paddingTop: '8px',
    }
}));


const Loading = React.memo(({ colSpan }) => (
    <TableRow>
        <TableCell colSpan={colSpan + 2} style={{ textAlign: 'center' }}>
            <div style={{ padding: 12 }}>
                <CircularProgress size={24} />
                <div style={{ padding: 10 }}>YÜKLENİYOR <br /> LÜTFEN BEKLEYİN</div>
            </div>
        </TableCell>
    </TableRow>
));

const NoResultFound = React.memo(({ colSpan, text }) => (
    <TableRow>
        <TableCell colSpan={colSpan + 2} style={{ textAlign: 'center' }}>
            <div style={{ fontWeight: 'bold', padding: 12 }}>{text}</div>
        </TableCell>
    </TableRow>
));

const LabelCell = React.memo(({ column, orderBy, handleOrderBy, hasOrderBy }) => {


    const field = column.orderByField || column.name;
    const sortable = column.sortable !== false;

    return (
        <StyledTableCell
            align={column.numeric ? 'right' : 'left'}
            width={column.width}
            sortDirection={orderBy?.name === field ? orderBy?.type : false}>

            {sortable && hasOrderBy &&
                <TableSortLabel
                    active={orderBy?.name === field}
                    direction={orderBy?.name === field ? orderBy?.type : 'asc'}
                    onClick={() => handleOrderBy(field)}>
                    {column.label}
                </TableSortLabel>
            }

            {!sortable && column.label}

        </StyledTableCell>
    );
});

const SearchCell = React.memo(({ column, defaultValue = "", placeholder, onSearch }) => {

    const [value, setValue] = useState(defaultValue);
    const process = useRef();

    const handleOnChange = (e) => {
        setValue(e.target.value);
        callSearchMethod(e.target.value);
    }

    const handleOnSelect = (e) => {
        setValue(e.target.value);
        callSearchMethod(e.target.value)
    }

    const clear = () => {
        setValue("");
        if (onSearch) {
            callSearchMethod(null);
        }
    }

    const onKeyPress = (e) => {
        if (e.charCode === 13 && onSearch) {
            callSearchMethod(value);
        }
    }

    const callSearchMethod = (val) => {

        if (process.current) {
            clearTimeout(process.current);
        }

        process.current = setTimeout(() => {
            const obj = {};

            if (!val || val === "") {
                obj[column.searchField || column.name] = undefined;
            } else {
                let converted = val;
                if (column.type) {
                    if (column.type === Array) {
                        converted = val.split(",");
                        if (Array.isArray(converted) && converted.length === 0) {
                            converted = undefined;
                        }

                    } else if (column.type === Boolean) {
                        converted = Boolean(Number(val));
                    } else {
                        converted = column.type(val);
                    }
                }
                obj[column.searchField || column.name] = converted;
            }
            onSearch(obj);
        }, 250);

    }

    return (
        <TableCell style={{
            backgroundColor: 'rgb(231, 228, 247)',
            margin: 0,
            padding: '0 4px 0 6px'
        }}>
            {column?.searchType === 'select' &&
                <SearchSelect
                    disableUnderline
                    value={value}
                    onChange={handleOnSelect}>
                    <option value="">Tümü</option>
                    {column.selectOptions}
                </SearchSelect>
            }

            {column?.searchType === 'custom' &&
                <column.searchComponent
                    value={value}
                    column={column}
                    onSearch={onSearch}
                    setValue={setValue}
                    setSearchData={callSearchMethod}
                />
            }

            {!column?.searchType &&
                <SearchInput
                    value={value}
                    onChange={handleOnChange}
                    onKeyPress={onKeyPress}
                    autoComplete="off"
                    placeholder={`${placeholder}...`}
                    type={column.type === Number ? 'number' : "text"}
                    startAdornment={
                        <SearchIcon fontSize="small" style={{ marginRight: 5 }} />
                    }
                    endAdornment={
                        value.length === 0 ? undefined :
                            <HighlightOffIcon
                                fontSize="small"
                                style={{ cursor: "pointer" }}
                                onClick={clear}
                            />
                    }
                />
            }
        </TableCell>
    );
});


const DefaultContainerComponent = (props) => {
    const classes = useStyles();
    const { t } = useTranslation();

    return (
        <Card className={classes.card}>
            <CardHeader
                title={props.title}
                style={props.headerStyle}
                classes={{ title: classes.title, root: classes.root }}
                subheader={props.subtitle}
            />
            {props.action && <Card
                style={{
                    margin: 5,
                    padding: 5,
                    paddingRight: 30,
                    display: 'flex',
                    justifyContent: 'flex-end', // aligns content to the right
                    overflow: 'auto',
                }}
            >

                <Box
                    style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 12,
                        justifyContent: 'flex-end', // aligns children to right
                        width: '100%',
                    }}
                >
                    {props.action}

                </Box>

            </Card>
            }

            {props?.selected &&
                <Card>
                    <Box style={{
                        padding: 10,
                        width: '100%',
                    }}>
                        {t('title.selected', { value: props.selected?.length || 0 })}
                    </Box>
                </Card>
            }

            {props.children}

        </Card>

    )
}

const DataTable = React.memo((
    {
        title,
        subtitle,
        data,
        loading,
        columns,
        count,
        page,
        setPage,
        orderBy,
        setOrderBy,
        max,
        setMax,
        searchData,
        setSearchData,
        action,
        selected,
        setSelected,
        setLoading,
        cardStyle,
        service,
        search,
        rowActions,
        headerStyle,
        rowStyle,
        detailView,
        ContainerComponent = DefaultContainerComponent
    }
) => {

    const { t } = useTranslation();

    const isSearchable = columns.some(item => item.searchable !== false);

    const [selectAll, setSelectAll] = useState(false);

    const [expanded, setExpanded] = useState(null);

    const resetSelection = () => {
        if (selectAll) {
            setSelectAll(false);
            setSelected([]);
        }
    };

    useEffect(() => {
        if (selectAll) {
            setSelected && setSelected([...data]);
        } else {
            setSelected && setSelected([]);
        }
    }, [selectAll, data]);

    useEffect(() => {
        resetSelection();
    }, [page, data]);

    const resolve = (item, column, rowIndex, colIndex) => {
        if (column.execute) {
            if (column.passItem) {
                return column.execute(item, rowIndex, colIndex);
            } else {
                return column.execute(item[column.name], rowIndex, colIndex);
            }
        }
        return item[column.name];
    }

    const resolveTitle = (item, column, rowIndex, colIndex) => {
        if (!column.component) {
            if (column.execute) {
                /*if (column.passItem) {
                    return column.execute(item, rowIndex, colIndex);
                } else {
                    return column.execute(item[column.name], rowIndex, colIndex);
                }*/
                return "";
            }
            return item[column.name];
        }
    }

    const handleOrderBy = (name) => {
        setOrderBy({
            name: name,
            type: orderBy?.name === name && orderBy?.type === 'asc' ? 'desc' : 'asc'
        });
        resetSelection();
    }

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
        resetSelection();
    };

    const handleChangeRowsPerPage = (event) => {
        setMax(parseInt(event.target.value, 10));
        setPage(1);
        resetSelection();
    };

    const isSelected = useCallback((item) => {
        return selected?.some(some => some.id === item.id);
    }, [selected]);

    const isExpanded = useCallback((item) => {
        return expanded?.id === item.id;
    }, [expanded])

    const expandItem = async (item) => {
        if (expanded?.id === item.id) {
            setExpanded(null);
        } else {
            setExpanded(item);
            await detailView.callDataSource(item);

        }
    }

    const isEnableExpand = (item) => {
        let isExpandedEnable = true;
        if (detailView?.viewEnable) {
            isExpandedEnable = detailView?.viewEnable(item);
        }
        return !isExpandedEnable;
    }

    const onSelect = (item, e) => {
        if (e.target.checked) {
            setSelected && setSelected([...selected, item]);
        } else {
            const items = selected?.filter(i => i.id !== item.id);
            setSelected && setSelected(items);
        }
    }

    const EmptyHeadCell = () => (
        <TableCell style={{
            backgroundColor: 'rgb(231, 228, 247)',
            margin: 0,
        }} />
    );

    const ExpandedHeadCell = () => (
        <TableCell style={{
            backgroundColor: 'rgb(231, 230, 234)',
            margin: 0,
            width: 25,
            maxWidth: 25
        }} />
    );

    const getDetayColSpan = (length) => {

        if (setSelected)
            length++;

        return length + 1
    }




    return (
        <>
            <ContainerComponent
                style={cardStyle}
                title={title}
                action={action}
                headerStyle={headerStyle}
                subheader={subtitle}
                selected={selected}>
                <TableContainer>
                    <Table size="small" className="data-table">
                        <TableHead>
                            <TableRow>
                                {detailView &&
                                    <EmptyHeadCell key="expanded" />}
                                {setSelected &&
                                    <StyledTableCell padding="checkbox" width={10} style={{ textAlign: 'center' }}>
                                        <Checkbox checked={selectAll} style={{ padding: 0 }}
                                            onChange={(e) => setSelectAll(e.target.checked)} />
                                    </StyledTableCell>
                                }
                                {columns.map((column, index) => (
                                    <LabelCell
                                        key={index}
                                        column={column}
                                        orderBy={orderBy}
                                        hasOrderBy={setOrderBy}
                                        handleOrderBy={handleOrderBy}
                                    />
                                ))}
                                {rowActions && <StyledTableCell width={10} />}
                            </TableRow>

                            {isSearchable &&
                                <TableRow>
                                    {detailView &&
                                        <ExpandedHeadCell key="expanded" />}
                                    {setSelected &&
                                        <EmptyHeadCell key="headerKey" />
                                    }
                                    {columns.map((column, index) => {
                                        const searchable = column.searchable !== false;
                                        // console.log(searchable);
                                        return (
                                            searchable ?
                                                <SearchCell
                                                    key={index}
                                                    placeholder={t('label.search')}
                                                    column={column}
                                                    defaultValue={column.defaultValue !== undefined ? column.defaultValue : ""}
                                                    onSearch={(data) => {

                                                        setSearchData({
                                                            ...searchData,
                                                            ...data
                                                        })
                                                    }}
                                                />
                                                : <EmptyHeadCell key={`empty-head-cell-${index}`} />
                                        )
                                    })}
                                    {rowActions && <EmptyHeadCell key="rowKey" />}
                                </TableRow>
                            }



                        </TableHead>

                        <TableBody>

                            {loading && <Loading colSpan={columns.length} />}

                            {data?.map((item, rowIndex) => {

                                const style = rowStyle ? rowStyle(item) : undefined;


                                return (
                                    <>
                                        <TableRow hover
                                            style={style}
                                            selected={isSelected(item)}>

                                            {detailView &&
                                                <TableCell>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => expandItem(item)}
                                                        style={{ textAlign: 'center' }}
                                                        disabled={isEnableExpand(item)}>
                                                        {isExpanded(item) ? <KeyboardArrowDown /> : <KeyboardArrowRight />}
                                                    </IconButton>
                                                </TableCell>}

                                            {setSelected &&
                                                <TableCell padding="checkbox" style={{ textAlign: 'center' }}>
                                                    <TableRowSelection selected={isSelected(item)} onSelect={onSelect}
                                                        item={item} />
                                                </TableCell>
                                            }

                                            {columns.map((column, colIndex) => (
                                                <TableCell key={colIndex}>
                                                    <div className="dataTableCell"
                                                        style={{ textAlign: column.textAlign ? column.textAlign : 'left' }}
                                                        title={resolveTitle(item, column, rowIndex, colIndex)}>
                                                        {resolve(item, column, rowIndex, colIndex)}
                                                    </div>
                                                </TableCell>
                                            ))}

                                            {rowActions &&
                                                <TableCell style={{ padding: 0, textAlign: "center" }}>
                                                    <DataTableManageButton
                                                        item={item}
                                                        rowIndex={rowIndex}
                                                        rowActions={rowActions}
                                                    />
                                                </TableCell>
                                            }
                                        </TableRow>

                                        <TableRow hover selected={isSelected(item)} sx={{ padding: 0, borderBottom: 'none' }} className="">
                                            <TableCell className="no-padding" colSpan={getDetayColSpan(columns.length)} sx={{ padding: 0, borderBottom: 'none' }}>
                                                <Collapse in={isExpanded(item)} timeout="auto" unmountOnExit sx={{ padding: 0 }}>
                                                    <Box sx={{ padding: 2, margin: 2 }}>
                                                        <Typography variant="body2" sx={{ textAlign: "center", padding: 0, margin: 0 }}>
                                                            {detailView?.viewObject}
                                                        </Typography>
                                                    </Box>
                                                </Collapse>
                                            </TableCell>
                                        </TableRow>

                                    </>
                                )
                            })}

                            {(!loading && count === 0) &&
                                <NoResultFound
                                    colSpan={columns.length}
                                    text={t('message.noResultFound')}
                                />
                            }
                        </TableBody>
                    </Table>
                </TableContainer>

                {!setPage &&
                    <div style={{ height: 10 }} />
                }

                {setPage &&
                    <TablePagination
                        rowsPerPageOptions={[10, 15, 25, 50]}
                        component="div"
                        count={count}
                        rowsPerPage={max}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                }

            </ContainerComponent>

        </>
    );
}
);

export {
    DataTable,
    StyledTableCell
}
